import { m } from '@pricedrop/i18n';
import { createFileRoute, notFound } from '@tanstack/react-router';
import X from 'lucide-react/dist/esm/icons/x';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { NotificationForm } from '@/components/notifications/notification-form';
import { Button } from '@/components/ui/button';
import { ApiError, api, fetchApi } from '@/lib/api';
import {
  EXTENSION_LOGIN_POPUP_QUERY_PARAM,
  EXTENSION_LOGIN_POPUP_QUERY_VALUE,
} from '@/lib/extension-login-popup';
import type { RawPriceEntry } from '@/lib/item-stats';
import { createExtensionNotificationsClient } from '@/lib/notifications/data-client';
import type { Notification } from '@/lib/notifications/types';
import { embedCspMiddleware } from '../-lib/embed-csp-middleware';
import { isAllowedParentOrigin, useExtensionAuth } from '../-lib/use-extension-auth';

const searchSchema = z.object({
  iframeId: z.string().default(''),
  parentOrigin: z.string().default(''),
});

export const Route = createFileRoute('/embed/notification-modal/$uin')({
  // Bearer-token + cookie-session dependent, both obtained client-side; SSR
  // cannot do anything useful here.
  ssr: false,
  server: {
    middleware: [embedCspMiddleware],
  },
  validateSearch: searchSchema,
  loader: async ({ params }) => {
    const uinNumber = Number(params.uin);
    if (!Number.isInteger(uinNumber)) throw notFound();
    // /api/items/:uin is public (no { auth: true }) so we can render the
    // modal title + current price even before the handshake completes.
    const item = await fetchApi(api.api.items({ uin: uinNumber }).get());
    const rawPrices = (item.prices ?? []) as RawPriceEntry[];
    const currentPrice = rawPrices.at(-1)?.price ?? null;
    return { uin: uinNumber, itemName: item.name, currentPrice };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: `${loaderData?.itemName ?? 'PriceDrop'} - Tracker` },
      { name: 'robots', content: 'noindex, nofollow' },
    ],
  }),
  component: TrackerModal,
});

function TrackerModal() {
  const { uin, itemName, currentPrice } = Route.useLoaderData();
  const { iframeId, parentOrigin } = Route.useSearch();
  // Validate the parentOrigin at the top-level once; useExtensionAuth also
  // checks this internally for its handshake, but the modal has several OTHER
  // postMessage call sites (resize / CLOSE_MODAL / NOTIFICATION_CHANGED) that
  // would otherwise trust the URL param verbatim.
  const originAllowed = isAllowedParentOrigin(parentOrigin);
  const { status, reportAuthExpired } = useExtensionAuth({
    iframeId,
    parentOrigin,
  });
  const [existing, setExisting] = useState<Notification | null>(null);
  const [existingLoaded, setExistingLoaded] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const token = status.state === 'authenticated' ? status.token : null;
  const tokenRef = useRef<string | null>(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const dataClient = useMemo(() => createExtensionNotificationsClient(() => tokenRef.current), []);

  // ResizeObserver → post height back to parent so the modal-iframe can
  // size itself. Guarded on `originAllowed` so we never postMessage to an
  // unvetted origin (e.g. if the page is loaded directly in a tab with a
  // forged parentOrigin search param).
  useEffect(() => {
    const el = rootRef.current;
    if (!el || !iframeId || !originAllowed) return;
    let lastHeight = 0;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const observer = new ResizeObserver(() => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const height = el.offsetHeight;
        if (Math.abs(height - lastHeight) >= 2) {
          lastHeight = height;
          window.parent.postMessage({ type: 'resize', height, iframeId }, parentOrigin);
        }
      }, 100);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [iframeId, parentOrigin, originAllowed]);

  // When authenticated, fetch the existing notification via bearer.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    dataClient
      .getForItem(uin)
      .then((n) => {
        if (cancelled) return;
        setExisting(n);
        setExistingLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          reportAuthExpired();
        }
        setExistingLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token, uin, dataClient, reportAuthExpired]);

  const postClose = useCallback(() => {
    if (!iframeId || !originAllowed) return;
    window.parent.postMessage({ type: 'CLOSE_MODAL', iframeId }, parentOrigin);
  }, [iframeId, parentOrigin, originAllowed]);

  const notifyChanged = useCallback(
    (action: 'saved' | 'deleted') => {
      if (!iframeId || !originAllowed) return;
      window.parent.postMessage(
        { type: 'NOTIFICATION_CHANGED', iframeId, itemUin: uin, action },
        parentOrigin,
      );
    },
    [iframeId, parentOrigin, uin, originAllowed],
  );

  // Esc key from within the iframe should close. Radix Dialog isn't
  // wrapping us (NotificationForm is rendered bare in variant="embed"),
  // so a global listener is appropriate. Gate on `defaultPrevented` so that
  // Radix popover dismissals (Select, Popover) don't bubble up and also
  // close the modal.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (e.defaultPrevented) return;
      postClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [postClose]);

  return (
    // Height is driven by natural content size; the parent iframe sizes
    // itself to match via the ResizeObserver+postMessage below. DO NOT cap
    // with max-h-screen / overflow-hidden here — that creates a feedback
    // loop where the iframe's viewport constrains its own content's
    // reported height and the modal gets stuck at the initial 400px.
    // The parent clamps to 90vh in content.ts's onResize; any content
    // overflow beyond that scrolls inside the iframe via its own scrollbar
    // (iframe.scrolling = 'auto' on the parent side for this modal kind).
    <div
      ref={rootRef}
      className="flex flex-col rounded-lg border bg-background shadow-lg"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pricedrop-tracker-modal-title"
    >
      {/* Modal chrome: title + close button */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h2 id="pricedrop-tracker-modal-title" className="truncate text-base font-semibold">
          {itemName}
        </h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={postClose}
          aria-label={m.extension_trackerModal_close()}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="p-4">
        {status.state === 'invalid-origin' ? (
          <div className="text-sm text-destructive" role="alert">
            {m.extension_trackerModal_invalidOrigin()}
          </div>
        ) : status.state === 'pending' ? (
          <div className="text-sm text-muted-foreground">{m.extension_trackerModal_loading()}</div>
        ) : status.state === 'unauthenticated' ? (
          <UnauthenticatedCta postClose={postClose} />
        ) : !existingLoaded ? (
          <div className="text-sm text-muted-foreground">{m.extension_trackerModal_loading()}</div>
        ) : (
          <NotificationForm
            itemUin={uin}
            itemName={itemName}
            currentPrice={currentPrice}
            existingNotification={existing}
            dataClient={dataClient}
            onSuccess={(n) => {
              setExisting(n);
              notifyChanged('saved');
              postClose();
            }}
            onDelete={() => {
              setExisting(null);
              notifyChanged('deleted');
              postClose();
            }}
            variant="embed"
          />
        )}
      </div>
    </div>
  );
}

/**
 * Renders the sign-in call-to-action when the user isn't authenticated.
 *
 * We don't embed the LoginForm directly because Better-Auth's session cookie
 * is `SameSite=Lax`, which the browser refuses to store when it's set from a
 * cross-site iframe (our iframe runs on localhost:4000 but its top frame is
 * ksp.co.il in prod). Instead: pop a window at /login?popup=extension.
 * Sign-in happens there (top-frame, same-site) and the cookie is stored
 * normally. The popup closes itself on success (see
 * apps/client/src/routes/_public/login.tsx).
 *
 * Once the popup opens, we close this modal. The user clicks "Track this
 * item" again when they're back; the fresh modal mount runs the auth
 * handshake against the now-valid cookie session and lands in the
 * authenticated state directly. Simpler than trying to keep the modal alive
 * and refresh it in place, which was fragile across message/timing boundaries.
 *
 * A future enhancement would switch Better-Auth to SameSite=None; Secure;
 * Partitioned so the iframe can sign in in place — at which point this
 * component can render LoginForm inline and the popup dance goes away.
 */
function UnauthenticatedCta({ postClose }: { postClose: () => void }) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const signInUrl =
    `${origin}/login?redirect=/account` +
    `&${EXTENSION_LOGIN_POPUP_QUERY_PARAM}=${EXTENSION_LOGIN_POPUP_QUERY_VALUE}`;
  const [popupBlocked, setPopupBlocked] = useState(false);

  const handleClick = () => {
    // Open the popup via a direct user gesture so popup blockers allow it.
    const win = window.open(signInUrl, 'pricedrop-login', 'width=480,height=640');
    if (!win) {
      // Popup was blocked (Firefox strict, Safari with extensions, enterprise
      // policy, etc.). Keep the modal open and surface an inline fallback
      // instead of silently closing.
      setPopupBlocked(true);
      return;
    }
    // Close the modal. When the user finishes signing in (popup auto-closes)
    // they click "Track this item" again; the fresh modal mount authenticates
    // cleanly via the normal handshake.
    postClose();
  };

  if (popupBlocked) {
    return (
      <div className="flex flex-col items-center gap-3 py-4 text-center">
        <p className="text-sm text-destructive" role="alert">
          {m.extension_trackerModal_popupBlocked()}
        </p>
        <a
          href={signInUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium underline"
        >
          {m.extension_trackerModal_openInNewTab()}
        </a>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3 py-4 text-center">
      <p className="text-sm text-muted-foreground">{m.extension_trackerModal_signInPrompt()}</p>
      <Button type="button" onClick={handleClick} className="w-full max-w-sm">
        {m.extension_trackerModal_signInCta()}
      </Button>
    </div>
  );
}
